// Desk-phone calls on the lead timeline (CU-869fmbgfq, contract ~/pm/contracts/V2-6.md v1.1).
// Dependency-free so Jest imports it without the components barrel. The ONLY place that
// knows the `voice_call` activity shape and the lead-call-suggestions route.
import { callsBase } from '../../../pages/dashboard/callLogs/callLogQuery';
import { formatUkPhone } from '../../../utils/ukPhone';
import { callLineLabel, cleanLineLabel } from '../../../utils/callLineLabel';

export const VOICE_CALL_VERB = 'voice_call';

export const isVoiceCall = (act) => act?.verb === VOICE_CALL_VERB;

const callOf = (act) => (act && typeof act.action_object === 'object' && act.action_object) || {};

// Every call entry the timeline shows with the one call component (TimelineCallEntry): desk-phone
// `voice_call` rows, and Twilio `call_logged` rows that carry a `call_id` (contract CU-869fmc5th:
// the same fields as a voice_call, plus the legacy ones). A `call_logged` row without a call id
// (WhatsApp calls) keeps its old "Inbound call - <outcome>" title.
export const isTimelineCall = (act) => {
    if (isVoiceCall(act)) return true;
    if (act?.verb !== 'call_logged') return false;
    const call = callOf(act);
    const hasId = (typeof call.call_id === 'number' || (typeof call.call_id === 'string' && call.call_id !== ''));
    return hasId && (call.direction === 'inbound' || call.direction === 'outbound') && typeof call.outcome === 'string' && call.outcome !== '';
};

// Same enum the call_logged rows use, so icons, labels and filters stay one table.
const OUTCOME_TO_LOGGED = {
    answered: 'ANSWERED',
    missed: 'NO_ANSWER',
    voicemail: 'VOICEMAIL',
    busy: 'BUSY',
    failed: 'FAILED',
};
export const voiceCallOutcome = (act) => OUTCOME_TO_LOGGED[callOf(act).outcome] || null;

// "+447700900123" → "07700 900123"; London "+442037104705" → "020 3710 4705".
// Anything that isn't a UK number is shown as stored. Display only.
export const formatUkNumber = (value) => formatUkPhone(typeof value === 'string' ? value.trim() : '');

// Same "4m 12s" as the call_logged rows (Activities.formatCallDuration).
export const formatDuration = (seconds) => {
    const n = Math.round(Number(seconds));
    if (!n || n < 0) return '';
    const m = Math.floor(n / 60);
    const s = n % 60;
    return m ? `${m}m ${s}s` : `${s}s`;
};

// "Missed call", "Outgoing call · 4m 12s", "Incoming call (in progress)".
export const voiceCallTitle = (act) => {
    const call = callOf(act);
    const inbound = call.direction !== 'outbound';
    switch (call.outcome) {
        case 'answered': {
            const dur = formatDuration(call.duration);
            const base = `${inbound ? 'Incoming' : 'Outgoing'} call`;
            return dur ? `${base} · ${dur}` : base;
        }
        case 'missed': return inbound ? 'Missed call' : 'Outgoing call · No answer';
        case 'voicemail': return inbound ? 'Incoming call · Voicemail' : 'Outgoing call · Voicemail';
        case 'busy': return `${inbound ? 'Incoming' : 'Outgoing'} call · Busy`;
        case 'failed': return `${inbound ? 'Incoming' : 'Outgoing'} call · Failed`;
        default: return `${inbound ? 'Incoming' : 'Outgoing'} call (in progress)`;
    }
};

const FOLLOW_UP_TEXT = {
    unresponded: 'Awaiting call-back',
    ignored: 'Call-back not needed',
    first_attempt: 'Call-back: try 1 of 3',
    second_attempt: 'Call-back: try 2 of 3',
    third_attempt: 'Call-back: try 3 of 3',
    responded: 'Called back',
};

// The line under the title: the other number, line, who/which desk, call-back progress.
export const voiceCallDetails = (act) => {
    const call = callOf(act);
    const parts = [];
    // The other party, the way staff say it; a withheld caller has none.
    const peer = formatUkNumber(call.peer_number);
    if (peer) parts.push(`${call.direction === 'outbound' ? 'To' : 'From'} ${peer}`);
    // The same line label as Call Logs: "Reading · Existing", not the connection name.
    const line = callLineLabel({ site: call.site_label, ringGroup: call.hunt_group, siteNumber: call.site_number })
        || cleanLineLabel(call.line_label);
    if (line) parts.push(`Line: ${line}`);
    const person = call.person?.name;
    const desk = call.desk?.name;
    if (person || desk) {
        const verb = call.direction === 'outbound' ? 'By' : 'Answered by';
        const who = person && desk ? `${person} (${desk})` : person || desk;
        if (call.direction === 'outbound' || call.outcome === 'answered') parts.push(`${verb} ${who}`);
        else if (desk) parts.push(`Rang ${desk}`);
    }
    const followUp = call.follow_up || {};
    if (followUp.result === 'couldnt_reach') parts.push("Couldn't reach after 3 tries");
    else if (FOLLOW_UP_TEXT[followUp.state] && call.direction === 'inbound' && call.outcome === 'missed') {
        parts.push(FOLLOW_UP_TEXT[followUp.state]);
    }
    if (act?.data?.data?.attached_before_lead) parts.push('Before this lead was created');
    return parts.join(' · ');
};

// What the viewer can open on the entry (the backend already strips what they may not see).
export const voiceCallMedia = (act) => {
    const call = callOf(act);
    const allowed = call.can_view_details === true;
    return {
        allowed,
        recording: allowed && call.recording_available === true && Boolean(call.recording_url),
        recordingUrl: allowed && call.recording_available === true && typeof call.recording_url === 'string' ? call.recording_url : '',
        transcript: allowed && call.transcript_available === true,
        summary: allowed && typeof call.summary === 'string' && call.summary.trim() ? call.summary.trim() : '',
        summaryPending: allowed && ['waiting_for_elevate', 'queued', 'pending'].includes(call.summary_status),
        rowId: typeof call.id === 'string' ? call.id : '',
    };
};

// An inbound call nobody answered (CU-869fn2wd1): credited to nobody, whatever actor or person
// the backend sends (an older backend names a fallback: the practice owner or a record editor).
// Covers the shared call entry and the legacy WhatsApp / Twilio `call_logged` shape.
const UNANSWERED = ['missed', 'voicemail', 'busy', 'failed'];
export const isUnansweredInboundCall = (act) => {
    const call = callOf(act);
    if (call.direction !== 'inbound') return false;
    if (isTimelineCall(act)) return UNANSWERED.includes(call.outcome);
    return act?.verb === 'call_logged' && typeof call.call_outcome === 'string' && call.call_outcome !== '' && call.call_outcome !== 'ANSWERED';
};

// Who was on the call (CRM user), '' when unknown or nobody answered: never the entry's fallback actor.
export const voiceCallPerson = (act) => {
    if (isUnansweredInboundCall(act)) return '';
    return typeof callOf(act).person?.name === 'string' ? callOf(act).person.name.trim() : '';
};

// The name a timeline row shows beside a call: the person on it, never the fallback actor.
export const callActorName = (act) => {
    if (isTimelineCall(act)) return voiceCallPerson(act);
    if (isUnansweredInboundCall(act)) return '';
    return `${act?.actor?.first_name || ''} ${act?.actor?.last_name || ''}`.trim();
};

export const isFailedVoiceCall = (act) => ['NO_ANSWER', 'BUSY', 'FAILED'].includes(voiceCallOutcome(act));

export const isOutboundVoiceCall = (act) => isTimelineCall(act) && callOf(act).direction === 'outbound';

// Words the timeline search should find for a call entry.
export const voiceCallSearchText = (act) => {
    const call = callOf(act);
    return [voiceCallTitle(act), voiceCallDetails(act), call.summary, call.peer_number, formatUkNumber(call.peer_number)]
        .filter((v) => typeof v === 'string' && v)
        .join(' ');
};

// ── Shared numbers: "Which of these was it?" ────────────────────────────────
export const SUGGESTIONS_URL = (practiceId, leadId) =>
    `${callsBase(practiceId)}lead-call-suggestions/?lead=${encodeURIComponent(leadId)}`;

export const suggestionsOf = (data) => (Array.isArray(data?.results) ? data.results : [])
    .filter((row) => row && typeof row.id === 'string' && row.id.startsWith('voice:'))
    .map((row) => ({
        id: row.id,
        startedAt: row.started_at || null,
        direction: row.direction === 'outbound' ? 'outbound' : 'inbound',
        outcome: row.outcome || '',
        duration: Number(row.duration) || 0,
        lineLabel: callLineLabel({ site: row.site_label, ringGroup: row.hunt_group, siteNumber: row.site_number }) || cleanLineLabel(row.line_label),
        number: formatUkNumber(row.peer_number || ''),
        otherLeads: Number(row.other_leads) || 0,
    }));

// A linkCall() result (callerActions.api) → what to tell the user. "uncertain" (no reply or a
// 5xx) may have saved: never call it a failure, and keep the button locked.
export const attachOutcome = (result) => {
    if (result?.ok) return { tone: 'success', message: 'Call added to this lead.', done: true };
    if (result?.uncertain) return { tone: 'info', message: "Couldn't confirm. Refresh to check before trying again.", locked: true };
    return { tone: 'error', message: result?.message || 'The call could not be attached.' };
};
